/**
 * EP-1 Hardware × Brand SLO Matrix — TanStack Query hooks.
 *
 * Backs the EP-1 sysadmin UI surfaces:
 *   /sysadmin/platform/hardware-profiles
 *   /sysadmin/tenants/$tenantId.hardware
 *   /sysadmin/models (compatibility column)
 *   /sysadmin/tenants/$tenantId.models (preflight verdict)
 *
 * Schemas live HERE so the wire types stay in lockstep with the
 * Fastify route schemas in packages/api/src/routes/v1/admin/ep1/*.
 */

import { z } from 'zod';
import {
  createSysadminQuery,
  createSysadminMutation,
} from './use-sysadmin-query';

// ─── Shared enums ────────────────────────────────────────────────────────

export const VendorSchema = z.enum(['nvidia', 'amd', 'intel', 'arm', 'cpu']);
export type Vendor = z.infer<typeof VendorSchema>;

export const AcceleratorModelSchema = z.enum([
  'a6000',
  'l40s',
  'h100',
  'h200',
  'mi300',
  'mi325',
  'strix_halo',
  'jetson_orin',
  'jetson_thor',
  'none',
  'other',
]);
export type AcceleratorModel = z.infer<typeof AcceleratorModelSchema>;

export const NetworkIsolationTierSchema = z.enum([
  'no_egress',
  'allowlist',
  'open',
]);
export type NetworkIsolationTier = z.infer<typeof NetworkIsolationTierSchema>;

export const PreflightVerdictSchema = z.enum(['green', 'yellow', 'red']);
export type PreflightVerdict = z.infer<typeof PreflightVerdictSchema>;

export const BaselineSourceSchema = z.enum(['measured', 'projected', 'manual']);
export type BaselineSource = z.infer<typeof BaselineSourceSchema>;

// ─── Hardware profile ────────────────────────────────────────────────────

export const HardwareProfileSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  displayName: z.string(),
  vendor: VendorSchema,
  acceleratorModel: AcceleratorModelSchema,
  vramGb: z.number().int(),
  systemRamGb: z.number().int(),
  networkIsolationTier: NetworkIsolationTierSchema,
  notes: z.string().nullable(),
  deletedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type HardwareProfile = z.infer<typeof HardwareProfileSchema>;

export const HardwareProfileWithCountsSchema = HardwareProfileSchema.extend({
  activeTenantCount: z.number().int(),
  baselineCount: z.number().int(),
});
export type HardwareProfileWithCounts = z.infer<
  typeof HardwareProfileWithCountsSchema
>;

export const HardwareProfileListResponseSchema = z.object({
  profiles: z.array(HardwareProfileWithCountsSchema),
});
export type HardwareProfileListResponse = z.infer<
  typeof HardwareProfileListResponseSchema
>;

export const CreateHardwareProfileSchema = z.object({
  slug: z
    .string()
    .min(2)
    .max(64)
    .regex(/^[a-z0-9_]+$/),
  displayName: z.string().min(1).max(200),
  vendor: VendorSchema,
  acceleratorModel: AcceleratorModelSchema,
  vramGb: z.number().int().min(0).max(10_000),
  systemRamGb: z.number().int().min(1).max(100_000),
  networkIsolationTier: NetworkIsolationTierSchema,
  notes: z.string().max(2000).nullable().optional(),
});
export type CreateHardwareProfile = z.infer<typeof CreateHardwareProfileSchema>;

export const useHardwareProfiles = createSysadminQuery({
  key: ['sysadmin-ep1-hardware-profiles'],
  url: '/admin/platform/hardware-profiles',
  schema: HardwareProfileListResponseSchema,
});

export const useCreateHardwareProfile = createSysadminMutation<
  CreateHardwareProfile,
  { id: string }
>({
  url: '/admin/platform/hardware-profiles',
  method: 'POST',
  inputSchema: CreateHardwareProfileSchema,
  outputSchema: z.object({ id: z.string().uuid() }),
  invalidates: [['sysadmin-ep1-hardware-profiles']],
});

// ─── Brand SLO baseline ──────────────────────────────────────────────────

export const BrandSloBaselineSchema = z.object({
  id: z.string().uuid(),
  brandId: z.string().uuid(),
  hardwareProfileId: z.string().uuid(),
  contextSizeTokens: z.number().int(),
  firstTokenP50Ms: z.number().int(),
  firstTokenP95Ms: z.number().int(),
  throughputTokPerSecP50: z.number(),
  maxConcurrentRequests: z.number().int(),
  source: BaselineSourceSchema,
  measuredBy: z.string().uuid().nullable(),
  measuredAt: z.string(),
  expiresAt: z.string(),
  notes: z.string().nullable(),
  createdAt: z.string(),
});
export type BrandSloBaseline = z.infer<typeof BrandSloBaselineSchema>;

export const BrandSloBaselineListResponseSchema = z.object({
  baselines: z.array(BrandSloBaselineSchema),
});

export const useBrandSloBaselines = createSysadminQuery({
  key: ['sysadmin-ep1-brand-slo-baselines'],
  url: '/admin/platform/brand-slo-baselines',
  schema: BrandSloBaselineListResponseSchema,
});

export const CreateBaselineSchema = z.object({
  brandId: z.string().uuid(),
  hardwareProfileId: z.string().uuid(),
  contextSizeTokens: z.number().int().min(1).max(1_000_000),
  firstTokenP50Ms: z.number().int().min(1).max(600_000),
  firstTokenP95Ms: z.number().int().min(1).max(600_000),
  throughputTokPerSecP50: z.number().min(0.01).max(10_000),
  maxConcurrentRequests: z.number().int().min(1).max(10_000),
  source: BaselineSourceSchema,
  notes: z.string().max(2000).nullable().optional(),
  expiresAt: z.string().datetime().optional(),
});
export type CreateBaseline = z.infer<typeof CreateBaselineSchema>;

export const useCreateBaseline = createSysadminMutation<
  CreateBaseline,
  { id: string }
>({
  url: '/admin/platform/brand-slo-baselines',
  method: 'POST',
  inputSchema: CreateBaselineSchema,
  outputSchema: z.object({ id: z.string().uuid() }),
  invalidates: [
    ['sysadmin-ep1-brand-slo-baselines'],
    ['sysadmin-ep1-hardware-profiles'],
  ],
});

// ─── Tenant hardware assignment ──────────────────────────────────────────

export const TenantHardwareAssignmentSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  hardwareProfileId: z.string().uuid(),
  validFrom: z.string(),
  validTo: z.string().nullable(),
  assignedBy: z.string().uuid(),
  notes: z.string().nullable(),
  createdAt: z.string(),
});
export type TenantHardwareAssignment = z.infer<
  typeof TenantHardwareAssignmentSchema
>;

export const HardwareProfileLiteSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  displayName: z.string(),
  vendor: z.string(),
  acceleratorModel: z.string(),
  vramGb: z.number().int(),
  systemRamGb: z.number().int(),
  networkIsolationTier: z.string(),
});

export const TenantHardwareResponseSchema = z.object({
  current: z
    .object({
      assignment: TenantHardwareAssignmentSchema,
      profile: HardwareProfileLiteSchema,
    })
    .nullable(),
  history: z.array(
    TenantHardwareAssignmentSchema.extend({
      profile: HardwareProfileLiteSchema,
    }),
  ),
});
export type TenantHardwareResponse = z.infer<typeof TenantHardwareResponseSchema>;

/**
 * Per-tenant hardware lookup. Path is dynamic (`/tenants/:id/hardware`)
 * so we use the `urlFn` form of `createSysadminQuery`.
 */
export const useTenantHardware = (tenantId: string) =>
  createSysadminQuery({
    key: ['sysadmin-ep1-tenant-hardware', tenantId],
    url: `/admin/platform/tenants/${tenantId}/hardware`,
    schema: TenantHardwareResponseSchema,
    // Guard: the Models page calls this with the selected tenant, which is
    // empty until the operator picks one. Without this, the query fired
    // `/tenants//hardware` (double slash) on first render → 422.
    enabled: tenantId.length > 0,
  })();

export const PutTenantHardwareSchema = z.object({
  hardwareProfileId: z.string().uuid(),
  notes: z.string().max(2000).optional(),
});
export type PutTenantHardware = z.infer<typeof PutTenantHardwareSchema>;

export const useUpdateTenantHardware = (tenantId: string) =>
  createSysadminMutation<
    PutTenantHardware,
    { id: string; priorAssignmentId: string | null }
  >({
    url: `/admin/platform/tenants/${tenantId}/hardware`,
    method: 'PUT',
    inputSchema: PutTenantHardwareSchema,
    outputSchema: z.object({
      id: z.string().uuid(),
      priorAssignmentId: z.string().uuid().nullable(),
    }),
    invalidates: [['sysadmin-ep1-tenant-hardware', tenantId]],
  })();

// ─── Preflight verdict ──────────────────────────────────────────────────

export const PreflightInputSchema = z.object({
  brandId: z.string().uuid(),
  contextSizeTokens: z.number().int().min(1).max(1_000_000).optional(),
  builderKind: z
    .enum([
      'ask_argus',
      'architect',
      'function_helper',
      'tool_helper',
      'skill_helper',
      'prompt_helper',
      'agent_runtime',
      'standalone_chat',
    ])
    .optional(),
});
export type PreflightInput = z.infer<typeof PreflightInputSchema>;

export const PreflightResponseSchema = z.object({
  verdict: PreflightVerdictSchema,
  reasoning: z.array(z.string()),
  hardwareProfile: HardwareProfileLiteSchema.nullable(),
  baseline: z
    .object({
      id: z.string().uuid(),
      contextSizeTokens: z.number().int(),
      firstTokenP50Ms: z.number().int(),
      firstTokenP95Ms: z.number().int(),
      throughputTokPerSecP50: z.number(),
      maxConcurrentRequests: z.number().int(),
      source: BaselineSourceSchema,
      measuredAt: z.string(),
      expiresAt: z.string(),
    })
    .nullable(),
  measuredHelperBudgetMs: z.number().int(),
  projectedFirstTokenP95Ms: z.number().int().nullable(),
  projectedThroughputTokPerSec: z.number().nullable(),
  builderKind: z.string(),
  contextSizeTokens: z.number().int(),
});
export type PreflightResponse = z.infer<typeof PreflightResponseSchema>;

/**
 * Preflight is a POST (not a query) because it carries a body. We expose
 * it as a mutation so callers can invoke it imperatively on every
 * dropdown change in the assignment dialog.
 */
export const usePreflightAssignment = (tenantId: string) =>
  createSysadminMutation<PreflightInput, PreflightResponse>({
    url: `/admin/platform/tenants/${tenantId}/preflight-assignment`,
    method: 'POST',
    inputSchema: PreflightInputSchema,
    outputSchema: PreflightResponseSchema,
    invalidates: [],
  })();

// ─── Risk-accept ─────────────────────────────────────────────────────────

export const RiskAcceptSchema = z.object({
  reason: z.string().min(20).max(2000),
  helperBudgetAcknowledgedMs: z.number().int().min(1).max(600_000),
});
export type RiskAccept = z.infer<typeof RiskAcceptSchema>;

export const useRiskAcceptAssignment = (tenantId: string, assignmentId: string) =>
  createSysadminMutation<
    RiskAccept,
    { id: string; verdict: PreflightVerdict; riskAcceptedAt: string }
  >({
    url: `/admin/platform/tenants/${tenantId}/argus-models/${assignmentId}/risk-accept`,
    method: 'POST',
    inputSchema: RiskAcceptSchema,
    outputSchema: z.object({
      id: z.string().uuid(),
      verdict: PreflightVerdictSchema,
      riskAcceptedAt: z.string(),
    }),
    invalidates: [['sysadmin-ep1-tenant-hardware', tenantId]],
  })();
